import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemBanner } from "../../../../components/UIToolkit/molecules/SystemBanner";
import type { SaveRecurringShiftHoursResult } from "../../../../services/backend.service";
import { SHIFT_HOURS_SAVE_SKIP_REASON_KEYS } from "./recurringDisplay";

type Props = { result: SaveRecurringShiftHoursResult };

/** Title key: a clean save, a save with something left out, or a save that wrote nothing at all. */
function titleKey(result: SaveRecurringShiftHoursResult): string {
	if (result.saved.length === 0) return "onboarding_load.recurring_shift_hours_save_result_none";
	if (result.skipped.length > 0) return "onboarding_load.recurring_shift_hours_save_result_partial";
	return "onboarding_load.recurring_shift_hours_save_result_saved";
}

/**
 * What the last save to Department Configuration did, job by job.
 *
 * A skipped job is named with its reason rather than folded into a count: every reason asks the
 * operator to do something different (edit the existing row, enable the job, or nothing at all).
 */
export const RecurringShiftHoursSaveResult: FC<Props> = ({ result }) => {
	const { t } = useTranslation();
	return (
		<SystemBanner
			tone={result.skipped.length > 0 ? "warning" : "success"}
			title={t(titleKey(result))}
			descriptionComponent="div"
			dataTestId="recurring-shift-hours-save-result"
			description={
				<Box component="ul" sx={{ m: 0, pl: 2.5 }}>
					{result.saved.length > 0 && (
						<li data-testid="recurring-shift-hours-save-result-saved">
							{t("onboarding_load.recurring_shift_hours_save_saved_list", { count: result.saved.length })}{" "}
							{result.saved.map((entry) => entry.jobName).join(", ")}
						</li>
					)}
					{result.skipped.length > 0 && (
						<li data-testid="recurring-shift-hours-save-result-skipped">
							{t("onboarding_load.recurring_shift_hours_save_skipped_list", { count: result.skipped.length })}
							<Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
								{result.skipped.map((entry) => (
									<li key={entry.jobName} data-testid={`recurring-shift-hours-save-skipped-${entry.jobName}`}>
										<strong>{entry.jobName}</strong> {t(SHIFT_HOURS_SAVE_SKIP_REASON_KEYS[entry.reason])}
									</li>
								))}
							</Box>
						</li>
					)}
				</Box>
			}
		/>
	);
};
